import { baseApi } from '../../../../store/api/baseApi';
import { NationalOverview, CompetencyBucket, LevelMetrics, TopPerformer, AlertItem, HolisticStudent } from '../types';
import { ACADEMIC_DOMAINS, FOUNDATIONAL_SCHEMA, parseCompetencyMap, getDomainScore } from '../stages/foundation/data/foundationalSchema';

type TermValue = 1 | 2;
export type TermFilter = TermValue | 'overall';

export const parseTermFilter = (raw: string | null | undefined): TermFilter => {
  if (raw === 'overall') return 'overall';
  return Number(raw || '1') === 2 ? 2 : 1;
};

export const termFilterToQueryValue = (term: TermFilter): string => (term === 'overall' ? 'overall' : String(term));

export interface NationalAnalyticsQueryArgs {
  academicYear: number;
  term: TermValue;
}

export interface RegionalAnalyticsQueryArgs extends NationalAnalyticsQueryArgs {
  regionId: number;
}

export interface StudentAnalyticsQueryArgs extends NationalAnalyticsQueryArgs {
  studentId: string;
}

export interface ClassAnalyticsQueryArgs extends NationalAnalyticsQueryArgs {
  classId: number;
  schoolId?: number;
}

export interface AnalyticsStudentRecord {
  sis_model_id: string;
  student_id: string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  first_name?: string;
  middle_name?: string;
  last_name?: string;
  academic_year: number;
  term: number;
  state_id: number;
  region_id: number;
  school_id: number;
  class_id: number;
  physical_dev_score: string | number | null;
  socio_emotional_score: string | number | null;
  cognitive_score: string | number | null;
  language_score: string | number | null;
  aesthetic_score: string | number | null;
  learning_habits_score: string | number | null;
  confidence_score: string | number | null;
  focus_score: string | number | null;
  peer_interaction_score: string | number | null;
  motivation_score: string | number | null;
  leadership_score: string | number | null;
  participation_score: string | number | null;
  schoolName?: string;
  schoolID?: number;
  kvID?: number;
  roName?: string;
  stateName?: string;
  className?: string;
  // Per-term competency JSON strings returned by analytics-class endpoint
  term1_competency?: string | null;
  term2_competency?: string | null;
  avgTerm1_competency?: number | null;
  avgTerm2_competency?: number | null;
}

export interface AnalyticsRegionPayload {
  region_id: number;
  roName: string;
  records: AnalyticsStudentRecord[];
}

export interface NationalAnalyticsResponse {
  success: boolean;
  academic_year: number;
  term: number;
  total: number;
  data: AnalyticsRegionPayload[];
  message?: string;
}

export interface RegionalAnalyticsResponse {
  success: boolean;
  academic_year: number;
  term: number;
  total: number;
  data: AnalyticsStudentRecord[];
  message?: string;
}

export interface AvgStudentAnalyticsResponse {
  success: boolean;
  academic_year: number;
  term: number;
  total: number;
  student_id: number | string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  gender?: string | number;
  rollNo?: number;
  section?: string;
  class_id?: number;
  className?: string;
  dob?: string;
  school_id?: number;
  schoolName?: string;
  roID?: number;
  avg_physical_dev_score?: string | number;
  avg_socio_emotional_score?: string | number;
  avg_cognitive_score?: string | number;
  avg_language_score?: string | number;
  avg_aesthetic_score?: string | number;
  avg_learning_habits_score?: string | number;
  avg_confidence_score?: string | number;
  avg_focus_score?: string | number;
  avg_peer_interaction_score?: string | number;
  avg_motivation_score?: string | number;
  avg_leadership_score?: string | number;
  avg_participation_score?: string | number;
  roName?: string;
  stateName?: string;
  hpcData?: {
    t1_height_handspan?: string | number;
    t2_height_handspan?: string | number;
    t1_weight_kg?: string | number;
    t2_weight_kg?: string | number;
    daysAttendedPerMonth?: string | Record<string, number | null>;
    totalSchoolDaysPerMonth?: string | Record<string, number | null>;
    term1_competency?: string | number | Record<string, number>;
    term2_competency?: string | number | Record<string, number>;
    things_i_like?: string;
    favourite_games?: string;
    favourite_food?: string;
    favourite_colour?: string;
    favourite_animals?: string;
    friends_are?: string;
    i_live_in?: string;
    lp_strengths_t1?: string;
    lp_strengths_t2?: string;
    lp_challenges_t1?: string;
    lp_challenges_t2?: string;
    lp_observations_t1?: string;
    lp_observations_t2?: string;
    lp_suggestions_t1?: string;
    lp_suggestions_t2?: string;
    parent_support_t1?: string;
    parent_support_t2?: string;
    parent_share_t1?: string;
    parent_share_t2?: string;
    parent_enjoys_t1?: string;
    parent_enjoys_t2?: string;
    parent_vaccine_t1?: string | number;
    parent_vaccine_t2?: string | number;
    self_with_friends_t1?: string;
    self_with_friends_t2?: string;
    self_find_difficult_t1?: string;
    self_find_difficult_t2?: string;
    self_enjoy_most_t1?: string;
    self_enjoy_most_t2?: string;
    peer_shares_stn_t1?: string | number;
    peer_shares_stn_t2?: string | number;
    peer_likes_play_t1?: string | number;
    peer_likes_play_t2?: string | number;
    peer_helps_task_t1?: string | number;
    peer_helps_task_t2?: string | number;
    photo_myself?: string | null;
    photo_family?: string | null;
    photo_portfolio?: string | null;
  };
  message?: string;
}

export interface ApiRegionSummary {
  regionId: number;
  regionName: string;
  stateCode: string;
  metrics: LevelMetrics;
  competencyBuckets: CompetencyBucket[];
  topPerformers: TopPerformer[];
  alerts: AlertItem[];
}

const toNumber = (value: string | number | null | undefined): number => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
};

/**
 * Auto-scale decimal scores (0–1 range) to 0–100 percentage.
 * Scores already in percent range (> 1) are returned as-is.
 */
export const scaleScore = (value: string | number | null | undefined): number => {
  const n = toNumber(value);
  if (n > 0 && n <= 1) return +(n * 100).toFixed(2);
  return n;
};

const avg = (values: number[]): number => {
  if (!values.length) return 0;
  return values.reduce((sum, n) => sum + n, 0) / values.length;
};

/**
 * Average only the non-zero values in the array.
 * This prevents missing/null fields from dragging the score down.
 * e.g. [33, 0, 0, 0, 0, 0] → 33 (not 5.5)
 */
const avgNonZero = (values: number[]): number => {
  const nonZero = values.filter(v => v > 0);
  if (!nonZero.length) return 0;
  return nonZero.reduce((sum, n) => sum + n, 0) / nonZero.length;
};

const getCompetencyLevel = (competency: number): CompetencyBucket['level'] => {
  if (competency >= 85) return 'Excellent';
  if (competency >= 70) return 'Good';
  if (competency >= 55) return 'Average';
  return 'Below Average';
};

const getSchoolId = (record: AnalyticsStudentRecord): number => {
  const raw = record.school_id ?? record.schoolID ?? record.kvID ?? -1;
  return Number.isFinite(Number(raw)) ? Number(raw) : -1;
};

const getClassId = (record: AnalyticsStudentRecord): number => {
  const raw = record.class_id ?? -1;
  return Number.isFinite(Number(raw)) ? Number(raw) : -1;
};

export const getClassDisplayName = (classId: number): string => {
  if (classId === -3) return 'Balvatika I';
  if (classId === -2) return 'Balvatika II';
  if (classId === -1) return 'Balvatika III';
  if (classId > 0) return `Class ${classId}`;
  return 'Class NA';
};

// ── Foundation Stage (Beginner / Progressing / Proficient) ───────────────────
export const FOUNDATION_PROFICIENT_PCT = (2.5 / 3) * 100; // ≈ 83.33
export const FOUNDATION_BEGINNER_PCT   = (1.5 / 3) * 100; // = 50.00

export const getFoundationCompetencyLevel = (
  pct: number,
): 'Proficient' | 'Progressing' | 'Beginner' => {
  if (pct >= FOUNDATION_PROFICIENT_PCT) return 'Proficient';
  if (pct >= FOUNDATION_BEGINNER_PCT)   return 'Progressing';
  return 'Beginner';
};

export const buildFoundationBuckets = (scores: number[]): CompetencyBucket[] => {
  const total = scores.length;
  let proficient = 0, progressing = 0, beginner = 0;
  scores.forEach(s => {
    const lvl = getFoundationCompetencyLevel(s);
    if (lvl === 'Proficient') proficient++;
    else if (lvl === 'Progressing') progressing++;
    else beginner++;
  });
  const pct = (n: number) => total ? +((n / total) * 100).toFixed(1) : 0;
  return [
    { level: 'Proficient',  count: proficient,  percent: pct(proficient)  },
    { level: 'Progressing', count: progressing, percent: pct(progressing) },
    { level: 'Beginner',    count: beginner,    percent: pct(beginner)    },
  ];
};

export interface CGBreakdownItem {
  cgNumber: number;
  label: string;
  shortLabel: string;
  avgRubric: number;
  avgPct: number;
  level: 'Proficient' | 'Progressing' | 'Beginner';
  studentCount: number;
}

export const getCGBreakdown = (
  records: AnalyticsStudentRecord[],
  term: TermFilter,
): CGBreakdownItem[] => {
  return FOUNDATIONAL_SCHEMA.map(cg => {
    let totalAvgRubric = 0;
    let studentCount = 0;

    records.forEach(record => {
      const m1 = parseCompetencyMap(record.term1_competency);
      const m2 = parseCompetencyMap(record.term2_competency);
      let cgSum = 0, cgCount = 0;

      cg.assessment.forEach(ind => {
        let score = 0;
        if (term === 1) {
          score = m1[ind.entryKey] || 0;
        } else if (term === 2) {
          score = m2[ind.entryKey] || 0;
        } else {
          const s1 = m1[ind.entryKey] || 0;
          const s2 = m2[ind.entryKey] || 0;
          score = s1 && s2 ? (s1 + s2) / 2 : s1 || s2;
        }
        if (score > 0) { cgSum += score; cgCount++; }
      });

      if (cgCount > 0) {
        totalAvgRubric += cgSum / cgCount;
        studentCount++;
      }
    });

    const avgRubric = studentCount > 0 ? totalAvgRubric / studentCount : 0;
    const avgPct    = (avgRubric / 3) * 100;

    return {
      cgNumber:     cg.s_no,
      label:        cg.label,
      shortLabel:   `CG-${cg.s_no}`,
      avgRubric:    +avgRubric.toFixed(2),
      avgPct:       +avgPct.toFixed(1),
      level:        getFoundationCompetencyLevel(avgPct),
      studentCount,
    };
  });
};

const buildBuckets = (scores: number[]): CompetencyBucket[] => {
  const total = scores.length;
  const counts: Record<CompetencyBucket['level'], number> = {
    Excellent: 0,
    Good: 0,
    Average: 0,
    'Below Average': 0,
  };

  scores.forEach((score) => {
    counts[getCompetencyLevel(score)] += 1;
  });

  const mk = (level: CompetencyBucket['level']): CompetencyBucket => ({
    level,
    count: counts[level],
    percent: total ? +((counts[level] / total) * 100).toFixed(1) : 0,
  });

  return [mk('Excellent'), mk('Good'), mk('Average'), mk('Below Average')];
};

interface DerivedRecordMetrics {
  competency: number;
  attendanceLike: number;
  behavior: number;
  peer: number;
  bmiNormalLike: number;
}

const competencyFromMap = (raw: string | null | undefined): number => {
  const map = parseCompetencyMap(raw);
  if (!Object.keys(map).length) return 0;
  const scores = ACADEMIC_DOMAINS.map(d => getDomainScore(d.cgNumbers, map)).filter(s => s > 0);
  return scores.length ? avg(scores) : 0;
};

export const deriveRecordMetrics = (
  record: AnalyticsStudentRecord,
  term?: TermFilter,
): DerivedRecordMetrics => {
  const t1 = competencyFromMap(record.term1_competency);
  const t2 = competencyFromMap(record.term2_competency);
  const hasT1 = t1 > 0;
  const hasT2 = t2 > 0;

  let competency = 0;
  const resolvedTerm = term !== undefined ? term : record.term;

  if (resolvedTerm === 1) {
    competency = hasT1 ? t1 : 0;
  } else if (resolvedTerm === 2) {
    competency = hasT2 ? t2 : 0;
  } else if (resolvedTerm === 'overall' || resolvedTerm === 0) {
    competency = hasT1 && hasT2 ? +((t1 + t2) / 2).toFixed(2) : hasT1 ? t1 : hasT2 ? t2 : 0;
  } else {
    competency = hasT1 ? t1 : hasT2 ? t2 : 0;
  }

  // Fallback to domain scores if competency is still 0
  if (competency === 0) {
    competency = avgNonZero([
      scaleScore(record.physical_dev_score),
      scaleScore(record.socio_emotional_score),
      scaleScore(record.cognitive_score),
      scaleScore(record.language_score),
      scaleScore(record.aesthetic_score),
      scaleScore(record.learning_habits_score),
    ]);
  }

  const attendanceLike = scaleScore(record.participation_score);

  const behavior = avgNonZero([
    scaleScore(record.confidence_score),
    scaleScore(record.focus_score),
    scaleScore(record.motivation_score),
    scaleScore(record.leadership_score),
  ]);

  const peer = scaleScore(record.peer_interaction_score);

  const physicalScore = scaleScore(record.physical_dev_score);
  // bmiNormalLike: physical score in healthy range (40-80%) after scaling
  const bmiNormalLike = physicalScore >= 40 && physicalScore <= 80 ? 1 : 0;

  return {
    competency,
    attendanceLike,
    behavior,
    peer,
    bmiNormalLike,
  };
};

export const summarizeMetrics = (records: AnalyticsStudentRecord[], term?: TermFilter): LevelMetrics => {
  const derived = records.map((record) => deriveRecordMetrics(record, term));
  const competencyScores = derived.map((d) => d.competency);

  const avgCompetency = avg(competencyScores);
  const avgAttendance = avg(derived.map((d) => d.attendanceLike));
  const avgBehaviorScore = avg(derived.map((d) => d.behavior));
  const avgPeerScore = avg(derived.map((d) => d.peer));

  const excellentCount = competencyScores.filter((s) => s >= 85).length;
  const needsSupportCount = competencyScores.filter((s) => s < 55).length;
  const bmiNormalPercent = records.length
    ? (derived.reduce((sum, d) => sum + d.bmiNormalLike, 0) / records.length) * 100
    : 0;

  return {
    totalStudents: records.length,
    avgAttendance: +avgAttendance.toFixed(1),
    avgCompetency: +avgCompetency.toFixed(1),
    needsSupportCount,
    excellentCount,
    avgBehaviorScore: +avgBehaviorScore.toFixed(1),
    avgPeerScore: +avgPeerScore.toFixed(1),
    bmiNormalPercent: +bmiNormalPercent.toFixed(1),
  };
};

const buildTopStudents = (
  records: AnalyticsStudentRecord[],
  idPrefix: string,
  term?: TermFilter,
): TopPerformer[] => {
  const scored = records
    .map((record) => ({
      record,
      competency: deriveRecordMetrics(record, term).competency,
    }))
    .sort((a, b) => b.competency - a.competency)
    .slice(0, 2);

  return scored.map((entry, index) => ({
    id: `${idPrefix}-T${index + 1}`,
    name: [
      entry.record.firstName || entry.record.first_name,
      entry.record.middleName || entry.record.middle_name,
      entry.record.lastName || entry.record.last_name,
    ].filter(Boolean).join(' ').trim() || `Student ${entry.record.student_id}`,
    metric: 'Competency',
    value: +entry.competency.toFixed(1),
  }));
};

const buildTopSchoolsByAverage = (
  records: AnalyticsStudentRecord[],
  idPrefix: string,
  term?: TermFilter,
): TopPerformer[] => {
  const schoolMap = new Map<number, { schoolName: string; sum: number; count: number }>();

  records.forEach((record) => {
    const schoolId = getSchoolId(record);
    if (schoolId < 0) return;

    const competency = deriveRecordMetrics(record, term).competency;
    const schoolName = record.schoolName || `School ${schoolId}`;
    const existing = schoolMap.get(schoolId);

    if (existing) {
      existing.sum += competency;
      existing.count += 1;
    } else {
      schoolMap.set(schoolId, { schoolName, sum: competency, count: 1 });
    }
  });

  return Array.from(schoolMap.values())
    .map((school) => ({
      name: school.schoolName,
      value: school.sum / Math.max(1, school.count),
    }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 2)
    .map((entry, index) => ({
      id: `${idPrefix}-T${index + 1}`,
      name: entry.name,
      metric: 'Avg Competency',
      value: +entry.value.toFixed(1),
    }));
};

const buildTopClassesByAverage = (
  records: AnalyticsStudentRecord[],
  idPrefix: string,
  term?: TermFilter,
): TopPerformer[] => {
  const classMap = new Map<number, { className: string; sum: number; count: number }>();

  records.forEach((record) => {
    const classId = getClassId(record);
    if (!Number.isFinite(classId)) return;

    const competency = deriveRecordMetrics(record, term).competency;
    const className = getClassDisplayName(classId);
    const existing = classMap.get(classId);

    if (existing) {
      existing.sum += competency;
      existing.count += 1;
    } else {
      classMap.set(classId, { className, sum: competency, count: 1 });
    }
  });

  return Array.from(classMap.values())
    .map((cls) => ({
      name: cls.className,
      value: cls.sum / Math.max(1, cls.count),
    }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 2)
    .map((entry, index) => ({
      id: `${idPrefix}-T${index + 1}`,
      name: entry.name,
      metric: 'Avg Competency',
      value: +entry.value.toFixed(1),
    }));
};

const buildRegionSummaryAlerts = (metrics: LevelMetrics, regionId: number, regionName: string): AlertItem[] => {
  const alerts: AlertItem[] = [];
  const supportRate = metrics.totalStudents ? (metrics.needsSupportCount / metrics.totalStudents) * 100 : 0;

  if (supportRate >= 30) {
    alerts.push({
      id: `R${regionId}-A1`,
      name: regionName,
      issue: `${supportRate.toFixed(1)}% students need support`,
      severity: 'high',
    });
  } else if (supportRate >= 18) {
    alerts.push({
      id: `R${regionId}-A1`,
      name: regionName,
      issue: `${supportRate.toFixed(1)}% students need support`,
      severity: 'medium',
    });
  }

  return alerts;
};

const toSupportSeverity = (supportRate: number): AlertItem['severity'] => {
  if (supportRate >= 35) return 'high';
  if (supportRate >= 20) return 'medium';
  return 'low';
};

const buildSchoolAlertsForRegion = (records: AnalyticsStudentRecord[], term?: TermFilter): AlertItem[] => {
  return getSchoolsFromRecords(records, term)
    .map((school) => {
      const supportRate = school.metrics.totalStudents
        ? (school.metrics.needsSupportCount / school.metrics.totalStudents) * 100
        : 0;
      return {
        id: `RA-${school.schoolId}`,
        name: school.schoolName,
        issue: `${school.metrics.needsSupportCount}/${school.metrics.totalStudents} need support (${supportRate.toFixed(1)}%)`,
        severity: toSupportSeverity(supportRate),
        supportRate,
      };
    })
    .filter((item) => item.supportRate >= 20)
    .sort((a, b) => b.supportRate - a.supportRate)
    .slice(0, 6)
    .map(({ supportRate: _supportRate, ...alert }) => alert);
};

const buildClassAlertsForSchool = (
  records: AnalyticsStudentRecord[],
  schoolId: number,
  term?: TermFilter,
): AlertItem[] => {
  return getClassesForSchoolFromRecords(records, String(schoolId), term)
    .map((cls) => {
      const supportRate = cls.metrics.totalStudents
        ? (cls.metrics.needsSupportCount / cls.metrics.totalStudents) * 100
        : 0;
      return {
        id: `SA-${schoolId}-${cls.classId}`,
        name: `${cls.className} ${cls.section}`.trim(),
        issue: `${cls.metrics.needsSupportCount}/${cls.metrics.totalStudents} need support (${supportRate.toFixed(1)}%)`,
        severity: toSupportSeverity(supportRate),
        supportRate,
      };
    })
    .filter((item) => item.supportRate >= 20)
    .sort((a, b) => b.supportRate - a.supportRate)
    .slice(0, 6)
    .map(({ supportRate: _supportRate, ...alert }) => alert);
};

const buildStudentAlertsForClass = (
  records: AnalyticsStudentRecord[],
  classId: number,
  term?: TermFilter,
): AlertItem[] => {
  return records
    .map((record) => {
      const metrics = deriveRecordMetrics(record, term);
      const fullName = [
        record.firstName || record.first_name,
        record.middleName || record.middle_name,
        record.lastName || record.last_name,
      ].filter(Boolean).join(' ').trim() || `Student ${record.student_id}`;

      return {
        id: `CA-${classId}-${record.student_id}`,
        name: fullName,
        issue: `Competency ${metrics.competency.toFixed(1)}%`,
        severity: metrics.competency < 45 ? 'high' as const : metrics.competency < 55 ? 'medium' as const : 'low' as const,
        competency: metrics.competency,
      };
    })
    .filter((item) => item.competency < 55)
    .sort((a, b) => a.competency - b.competency)
    .slice(0, 6)
    .map(({ competency: _competency, ...alert }) => alert);
};

export interface ApiSchoolSummary {
  schoolId: string;
  schoolName: string;
  location: string;
  metrics: LevelMetrics;
  competencyBuckets: CompetencyBucket[];
  topPerformers: TopPerformer[];
  alerts: AlertItem[];
}

export interface ApiClassSummary {
  classId: string;
  className: string;
  section: string;
  teacherName: string;
  metrics: LevelMetrics;
  competencyBuckets: CompetencyBucket[];
  topPerformers: TopPerformer[];
  alerts: AlertItem[];
}

export const getRegionSummaryFromRecords = (
  records: AnalyticsStudentRecord[],
  regionId: number,
  term?: TermFilter,
): ApiRegionSummary => {
  const metrics = summarizeMetrics(records, term);
  const competencyScores = records.map((record) => deriveRecordMetrics(record, term).competency);

  return {
    regionId,
    regionName: records[0]?.roName || `Region ${regionId}`,
    stateCode: records[0]?.stateName || 'NA',
    metrics,
    competencyBuckets: buildBuckets(competencyScores),
    topPerformers: buildTopSchoolsByAverage(records, `R${regionId}`, term),
    alerts: buildSchoolAlertsForRegion(records, term),
  };
};

export const getSchoolsFromRecords = (records: AnalyticsStudentRecord[], term?: TermFilter): ApiSchoolSummary[] => {
  const schoolMap = new Map<number, AnalyticsStudentRecord[]>();
  records.forEach((record) => {
    const key = getSchoolId(record);
    if (key < 0) return;
    const current = schoolMap.get(key) || [];
    current.push(record);
    schoolMap.set(key, current);
  });

  return Array.from(schoolMap.entries()).map(([schoolId, schoolRecords]) => {
    const metrics = summarizeMetrics(schoolRecords, term);
    const competencyScores = schoolRecords.map((record) => deriveRecordMetrics(record, term).competency);
    const schoolName = schoolRecords[0]?.schoolName || `School ${schoolId}`;

    return {
      schoolId: String(schoolId),
      schoolName,
      location: schoolRecords[0]?.stateName || 'NA',
      metrics,
      competencyBuckets: buildBuckets(competencyScores),
      topPerformers: buildTopClassesByAverage(schoolRecords, `S${schoolId}`, term),
      alerts: buildClassAlertsForSchool(schoolRecords, schoolId, term),
    };
  }).sort((a, b) => b.metrics.totalStudents - a.metrics.totalStudents);
};

export const getSchoolByIdFromRecords = (records: AnalyticsStudentRecord[], schoolId: string, term?: TermFilter): ApiSchoolSummary | undefined => {
  return getSchoolsFromRecords(records, term).find((school) => school.schoolId === schoolId);
};

export const getClassesForSchoolFromRecords = (records: AnalyticsStudentRecord[], schoolId: string, term?: TermFilter): ApiClassSummary[] => {
  const targetSchoolId = Number(schoolId);
  const schoolRecords = records.filter((record) => getSchoolId(record) === targetSchoolId);

  const classMap = new Map<number, AnalyticsStudentRecord[]>();
  schoolRecords.forEach((record) => {
    const key = getClassId(record);
    const current = classMap.get(key) || [];
    current.push(record);
    classMap.set(key, current);
  });

  return Array.from(classMap.entries()).map(([classId, classRecords]) => {
    const metrics = summarizeMetrics(classRecords, term);
    const competencyScores = classRecords.map((record) => deriveRecordMetrics(record, term).competency);
    const classLabel = getClassDisplayName(classId);

    return {
      classId: String(classId),
      className: classLabel,
      section: '-',
      teacherName: 'N/A',
      metrics,
      competencyBuckets: buildBuckets(competencyScores),
      topPerformers: buildTopStudents(classRecords, `C${classId}`, term),
      alerts: buildStudentAlertsForClass(classRecords, classId, term),
    };
  }).sort((a, b) => b.metrics.totalStudents - a.metrics.totalStudents);
};

export const getClassByIdFromRecords = (records: AnalyticsStudentRecord[], schoolId: string, classId: string, term?: TermFilter): ApiClassSummary | undefined => {
  return getClassesForSchoolFromRecords(records, schoolId, term).find((cls) => cls.classId === classId);
};

export const getStudentsForClassFromRecords = (records: AnalyticsStudentRecord[], schoolId: string, classId: string): AnalyticsStudentRecord[] => {
  return records.filter((record) => String(getSchoolId(record)) === schoolId && String(getClassId(record)) === classId);
};

export const getRegionPayloadById = (
  payload: NationalAnalyticsResponse | undefined,
  regionId: number,
): AnalyticsRegionPayload | undefined => payload?.data?.find((region) => region.region_id === regionId);

export const getSchoolsForRegionFromApi = (
  payload: NationalAnalyticsResponse | undefined,
  regionId: number,
  term?: TermFilter,
): ApiSchoolSummary[] => {
  const region = getRegionPayloadById(payload, regionId);
  if (!region) return [];

  const schoolMap = new Map<number, AnalyticsStudentRecord[]>();
  (region.records || []).forEach((record) => {
    const key = getSchoolId(record);
    if (key < 0) return;
    const current = schoolMap.get(key) || [];
    current.push(record);
    schoolMap.set(key, current);
  });

  return Array.from(schoolMap.entries()).map(([schoolId, records], index) => {
    const metrics = summarizeMetrics(records, term);
    const competencyScores = records.map((record) => deriveRecordMetrics(record, term).competency);
    const schoolName = records[0]?.schoolName || `School ${schoolId}`;

    return {
      schoolId: String(schoolId),
      schoolName,
      location: records[0]?.stateName || 'NA',
      metrics,
      competencyBuckets: buildBuckets(competencyScores),
      topPerformers: buildTopClassesByAverage(records, `S${schoolId}`, term),
      alerts: buildClassAlertsForSchool(records, schoolId, term),
    };
  }).sort((a, b) => b.metrics.totalStudents - a.metrics.totalStudents);
};

export const getSchoolByIdFromApi = (
  payload: NationalAnalyticsResponse | undefined,
  regionId: number,
  schoolId: string,
  term?: TermFilter,
): ApiSchoolSummary | undefined => {
  const schools = getSchoolsForRegionFromApi(payload, regionId, term);
  return schools.find((school) => school.schoolId === schoolId);
};

export const getClassesForSchoolFromApi = (
  payload: NationalAnalyticsResponse | undefined,
  regionId: number,
  schoolId: string,
  term?: TermFilter,
): ApiClassSummary[] => {
  const region = getRegionPayloadById(payload, regionId);
  if (!region) return [];

  const targetSchoolId = Number(schoolId);
  const schoolRecords = (region.records || []).filter((record) => getSchoolId(record) === targetSchoolId);

  const classMap = new Map<number, AnalyticsStudentRecord[]>();
  schoolRecords.forEach((record) => {
    const key = getClassId(record);
    const current = classMap.get(key) || [];
    current.push(record);
    classMap.set(key, current);
  });

  return Array.from(classMap.entries()).map(([classId, records]) => {
    const metrics = summarizeMetrics(records, term);
    const competencyScores = records.map((record) => deriveRecordMetrics(record, term).competency);
    const classLabel = getClassDisplayName(classId);

    return {
      classId: String(classId),
      className: classLabel,
      section: '-',
      teacherName: 'N/A',
      metrics,
      competencyBuckets: buildBuckets(competencyScores),
      topPerformers: buildTopStudents(records, `C${classId}`, term),
      alerts: buildStudentAlertsForClass(records, classId, term),
    };
  }).sort((a, b) => b.metrics.totalStudents - a.metrics.totalStudents);
};

export const getClassByIdFromApi = (
  payload: NationalAnalyticsResponse | undefined,
  regionId: number,
  schoolId: string,
  classId: string,
  term?: TermFilter,
): ApiClassSummary | undefined => {
  const classes = getClassesForSchoolFromApi(payload, regionId, schoolId, term);
  return classes.find((cls) => cls.classId === classId);
};

export const getStudentsForClassFromApi = (
  payload: NationalAnalyticsResponse | undefined,
  regionId: number,
  schoolId: string,
  classId: string,
): AnalyticsStudentRecord[] => {
  const region = getRegionPayloadById(payload, regionId);
  if (!region) return [];

  return (region.records || []).filter((record) => (
    String(getSchoolId(record)) === schoolId && String(getClassId(record)) === classId
  ));
};

const emptyOverview: NationalOverview = {
  metrics: {
    totalStudents: 0,
    avgAttendance: 0,
    avgCompetency: 0,
    needsSupportCount: 0,
    excellentCount: 0,
    avgBehaviorScore: 0,
    avgPeerScore: 0,
    bmiNormalPercent: 0,
  },
  competencyBuckets: [
    { level: 'Excellent', count: 0, percent: 0 },
    { level: 'Good', count: 0, percent: 0 },
    { level: 'Average', count: 0, percent: 0 },
    { level: 'Below Average', count: 0, percent: 0 },
  ],
  attendanceTrend: [],
  regions: [],
  topPerformers: [],
  alerts: [],
};

export const mapNationalAnalyticsToOverview = (
  payload: NationalAnalyticsResponse | undefined,
  term?: TermFilter,
): NationalOverview => {
  if (!payload?.success || !Array.isArray(payload.data)) {
    return emptyOverview;
  }

  const regions = payload.data.map((region) => {
    const metrics = summarizeMetrics(region.records || [], term);
    const competencyScores = (region.records || []).map((r) => deriveRecordMetrics(r, term).competency);
    const stateName = region.records?.[0]?.stateName || 'NA';

    return {
      regionId: region.region_id,
      regionName: region.roName,
      stateCode: stateName,
      metrics,
      competencyBuckets: buildBuckets(competencyScores),
      topPerformers: buildTopSchoolsByAverage(region.records || [], `R${region.region_id}`, term),
      alerts: buildRegionSummaryAlerts(metrics, region.region_id, region.roName),
    };
  });

  const allRecords = payload.data.flatMap((region) => region.records || []);
  const allDerived = allRecords.map((r) => deriveRecordMetrics(r, term));

  const metrics = summarizeMetrics(allRecords, term);
  const competencyBuckets = buildBuckets(allDerived.map((d) => d.competency));

  const topPerformers: TopPerformer[] = [...regions]
    .sort((a, b) => b.metrics.avgCompetency - a.metrics.avgCompetency)
    .slice(0, 3)
    .map((region, index) => ({
      id: `N-T${index + 1}`,
      name: `${region.regionName} Region`,
      metric: 'Avg Competency',
      value: region.metrics.avgCompetency,
    }));

  const alerts: AlertItem[] = [...regions]
    .filter((region) => region.metrics.totalStudents > 0)
    .sort((a, b) => {
      const rateA = a.metrics.needsSupportCount / a.metrics.totalStudents;
      const rateB = b.metrics.needsSupportCount / b.metrics.totalStudents;
      return rateB - rateA;
    })
    .slice(0, 3)
    .map((region, index) => {
      const supportRate = (region.metrics.needsSupportCount / Math.max(1, region.metrics.totalStudents)) * 100;
      return {
        id: `N-A${index + 1}`,
        name: `${region.regionName} Region`,
        issue: `${supportRate.toFixed(1)}% students need support`,
        severity: supportRate >= 30 ? 'high' : supportRate >= 18 ? 'medium' : 'low',
      } as AlertItem;
    });

  return {
    metrics,
    competencyBuckets,
    attendanceTrend: [],
    regions,
    topPerformers,
    alerts,
  };
};

export const summarizeFoundationMetrics = (
  records: AnalyticsStudentRecord[],
  term?: TermFilter,
): LevelMetrics => {
  const base = summarizeMetrics(records, term);
  const scores = records.map(r => deriveRecordMetrics(r, term).competency);
  return {
    ...base,
    excellentCount:    scores.filter(s => s >= FOUNDATION_PROFICIENT_PCT).length,
    needsSupportCount: scores.filter(s => s <  FOUNDATION_BEGINNER_PCT).length,
  };
};

export const getFoundationRegionSummary = (
  records: AnalyticsStudentRecord[],
  regionId: number,
  term?: TermFilter,
): ApiRegionSummary => {
  const base   = getRegionSummaryFromRecords(records, regionId, term);
  const scores = records.map(r => deriveRecordMetrics(r, term).competency);
  return {
    ...base,
    metrics:           summarizeFoundationMetrics(records, term),
    competencyBuckets: buildFoundationBuckets(scores),
  };
};

export const getFoundationSchoolsFromRecords = (
  records: AnalyticsStudentRecord[],
  term?: TermFilter,
): ApiSchoolSummary[] => {
  return getSchoolsFromRecords(records, term).map(school => {
    const students = records.filter(r => String(getSchoolId(r)) === school.schoolId);
    const scores   = students.map(r => deriveRecordMetrics(r, term).competency);
    return {
      ...school,
      metrics:           summarizeFoundationMetrics(students, term),
      competencyBuckets: buildFoundationBuckets(scores),
    };
  });
};

export const getFoundationClassesForSchoolFromRecords = (
  records: AnalyticsStudentRecord[],
  schoolId: string,
  term?: TermFilter,
): ApiClassSummary[] => {
  return getClassesForSchoolFromRecords(records, schoolId, term).map(cls => {
    const students = getStudentsForClassFromRecords(records, schoolId, cls.classId);
    const scores   = students.map(r => deriveRecordMetrics(r, term).competency);
    return {
      ...cls,
      metrics:           summarizeFoundationMetrics(students, term),
      competencyBuckets: buildFoundationBuckets(scores),
    };
  });
};

export const getFoundationClassByIdFromRecords = (
  records: AnalyticsStudentRecord[],
  schoolId: string,
  classId: string,
  term?: TermFilter,
): ApiClassSummary | undefined => {
  const cls = getClassByIdFromRecords(records, schoolId, classId, term);
  if (!cls) return undefined;
  const students = getStudentsForClassFromRecords(records, schoolId, classId);
  const scores   = students.map(r => deriveRecordMetrics(r, term).competency);
  return {
    ...cls,
    metrics:           summarizeFoundationMetrics(students, term),
    competencyBuckets: buildFoundationBuckets(scores),
  };
};

export const mapNationalAnalyticsToFoundationOverview = (
  payload: NationalAnalyticsResponse | undefined,
  term?: TermFilter,
): NationalOverview => {
  if (!payload?.success || !Array.isArray(payload.data)) return emptyOverview;

  const regions = payload.data.map(region => {
    const recs    = region.records || [];
    const metrics = summarizeFoundationMetrics(recs, term);
    const scores  = recs.map(r => deriveRecordMetrics(r, term).competency);
    return {
      regionId:          region.region_id,
      regionName:        region.roName,
      stateCode:         recs[0]?.stateName || 'NA',
      metrics,
      competencyBuckets: buildFoundationBuckets(scores),
      topPerformers:     buildTopSchoolsByAverage(recs, `R${region.region_id}`, term),
      alerts:            buildRegionSummaryAlerts(metrics, region.region_id, region.roName),
    };
  });

  const allRecords = payload.data.flatMap(r => r.records || []);
  const allScores  = allRecords.map(r => deriveRecordMetrics(r, term).competency);
  const metrics    = summarizeFoundationMetrics(allRecords, term);

  const topPerformers: TopPerformer[] = [...regions]
    .sort((a, b) => b.metrics.avgCompetency - a.metrics.avgCompetency)
    .slice(0, 3)
    .map((r, i) => ({ id: `N-T${i + 1}`, name: `${r.regionName} Region`, metric: 'Avg Competency', value: r.metrics.avgCompetency }));

  const alerts: AlertItem[] = [...regions]
    .filter(r => r.metrics.totalStudents > 0)
    .sort((a, b) => (b.metrics.needsSupportCount / b.metrics.totalStudents) - (a.metrics.needsSupportCount / a.metrics.totalStudents))
    .slice(0, 3)
    .map((r, i) => {
      const rate = (r.metrics.needsSupportCount / Math.max(1, r.metrics.totalStudents)) * 100;
      return {
        id: `N-A${i + 1}`,
        name: `${r.regionName} Region`,
        issue: `${rate.toFixed(1)}% students need support`,
        severity: rate >= 30 ? 'high' : rate >= 18 ? 'medium' : 'low',
      } as AlertItem;
    });

  return {
    metrics,
    competencyBuckets: buildFoundationBuckets(allScores),
    attendanceTrend:   [],
    regions,
    topPerformers,
    alerts,
  };
};

const toGender = (raw: string | number | undefined): 'Male' | 'Female' | 'Other' => {
  if (String(raw) === '1') return 'Male';
  if (String(raw) === '2') return 'Female';
  return 'Other';
};

const toCompetencyLevel = (value: number): 'Excellent' | 'Good' | 'Average' | 'Below Average' => {
  if (value >= 85) return 'Excellent';
  if (value >= 70) return 'Good';
  if (value >= 55) return 'Average';
  return 'Below Average';
};

const toText = (value: unknown, fallback = ''): string => {
  if (value === null || value === undefined) return fallback;
  const v = String(value).trim();
  return v || fallback;
};

const toVaccineLabel = (value: unknown): string => {
  const normalized = String(value ?? '').trim().toLowerCase();
  if (normalized === '1' || normalized === 'yes' || normalized === 'true') return 'Yes';
  if (normalized === '0' || normalized === 'no' || normalized === 'false') return 'No';
  return toText(value, '-');
};

const parseJsonObject = (value: unknown): Record<string, number | null> => {
  if (!value) return {};
  if (typeof value === 'object' && !Array.isArray(value)) {
    return Object.entries(value as Record<string, unknown>).reduce<Record<string, number | null>>((acc, [k, v]) => {
      if (v === null || v === undefined || v === '') {
        acc[k] = null;
      } else {
        const n = Number(v);
        acc[k] = Number.isFinite(n) ? n : null;
      }
      return acc;
    }, {});
  }
  if (typeof value !== 'string') return {};
  try {
    const parsed = JSON.parse(value);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    return Object.entries(parsed as Record<string, unknown>).reduce<Record<string, number | null>>((acc, [k, v]) => {
      if (v === null || v === undefined || v === '') {
        acc[k] = null;
      } else {
        const n = Number(v);
        acc[k] = Number.isFinite(n) ? n : null;
      }
      return acc;
    }, {});
  } catch {
    return {};
  }
};

export const parseCompetencyValue = (value: unknown): number => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  if (typeof value === 'string') {
    const formatted = value.trim().replace(/%$/, '');
    const asNumber = Number(formatted);
    if (Number.isFinite(asNumber)) return asNumber;

    const parsed = parseJsonObject(value);
    const vals = Object.values(parsed).filter((n): n is number => typeof n === 'number' && Number.isFinite(n));
    if (!vals.length) return 0;
    const maxValue = Math.max(...vals);
    const achieved = vals.reduce((sum, n) => sum + n, 0);

    // Normalize rubric-style scores (e.g. 0-3, 0-4, 0-5) to percentage.
    if (maxValue <= 10) {
      const rubricMax = maxValue <= 3 ? 3 : maxValue <= 4 ? 4 : maxValue <= 5 ? 5 : 10;
      return +((achieved / (vals.length * rubricMax)) * 100).toFixed(1);
    }

    // Values already in percent-like range.
    return +(achieved / vals.length).toFixed(1);
  }
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const vals = Object.values(value as Record<string, unknown>)
      .map((v) => Number(v))
      .filter((n) => Number.isFinite(n));
    if (!vals.length) return 0;
    const maxValue = Math.max(...vals);
    const achieved = vals.reduce((sum, n) => sum + n, 0);

    if (maxValue <= 10) {
      const rubricMax = maxValue <= 3 ? 3 : maxValue <= 4 ? 4 : maxValue <= 5 ? 5 : 10;
      return +((achieved / (vals.length * rubricMax)) * 100).toFixed(1);
    }

    return +(achieved / vals.length).toFixed(1);
  }
  return 0;
};

export const getTermComparisonData = (records: AnalyticsStudentRecord[]): { name: string; 'Term 1': number; 'Term 2': number }[] => {
  let t1Sum = 0, t1Count = 0;
  let t2Sum = 0, t2Count = 0;

  records.forEach((record) => {
    // Prefer rubric JSON map; fall back to pre-aggregated numeric fields
    let t1 = parseCompetencyValue(record.term1_competency);
    if (!t1) t1 = toNumber(record.avgTerm1_competency);

    let t2 = parseCompetencyValue(record.term2_competency);
    if (!t2) t2 = toNumber(record.avgTerm2_competency);

    if (t1 > 0) { t1Sum += t1; t1Count++; }
    if (t2 > 0) { t2Sum += t2; t2Count++; }
  });

  const term1Avg = t1Count ? +(t1Sum / t1Count).toFixed(1) : 0;
  const term2Avg = t2Count ? +(t2Sum / t2Count).toFixed(1) : 0;

  return [
    {
      name: 'Competency',
      'Term 1': term1Avg,
      'Term 2': term2Avg,
    },
  ];
};

export const getActualSubjectBreakdown = (
  records: AnalyticsStudentRecord[],
  termValue: TermFilter,
): { subject: string; avgScore: number }[] => {
  return ACADEMIC_DOMAINS.map((domain) => {
    let sum = 0;
    let count = 0;
    records.forEach((record) => {
      const t1 = parseCompetencyMap(record.term1_competency);
      const t2 = parseCompetencyMap(record.term2_competency);

      let score = 0;
      if (termValue === 1) {
        score = getDomainScore(domain.cgNumbers, t1);
      } else if (termValue === 2) {
        score = getDomainScore(domain.cgNumbers, t2);
      } else {
        const s1 = getDomainScore(domain.cgNumbers, t1);
        const s2 = getDomainScore(domain.cgNumbers, t2);
        if (s1 > 0 && s2 > 0) score = (s1 + s2) / 2;
        else score = s1 || s2;
      }

      if (score > 0) {
        sum += score;
        count++;
      }
    });

    const avgScore = count ? Math.round(sum / count) : 0;
    return {
      subject: domain.label,
      avgScore,
    };
  }).filter((d) => d.avgScore > 0);
};

type ScoreField =
  | 'physical_dev_score'
  | 'socio_emotional_score'
  | 'cognitive_score'
  | 'language_score'
  | 'aesthetic_score'
  | 'learning_habits_score'
  | 'confidence_score'
  | 'focus_score'
  | 'peer_interaction_score'
  | 'motivation_score'
  | 'leadership_score'
  | 'participation_score';

const SCORE_FIELDS: ScoreField[] = [
  'physical_dev_score',
  'socio_emotional_score',
  'cognitive_score',
  'language_score',
  'aesthetic_score',
  'learning_habits_score',
  'confidence_score',
  'focus_score',
  'peer_interaction_score',
  'motivation_score',
  'leadership_score',
  'participation_score',
];

const mergeNumeric = (
  a: string | number | null | undefined,
  b: string | number | null | undefined,
): number => {
  const hasA = a !== null && a !== undefined && a !== '';
  const hasB = b !== null && b !== undefined && b !== '';
  if (hasA && hasB) return +((toNumber(a) + toNumber(b)) / 2).toFixed(1);
  if (hasA) return toNumber(a);
  return toNumber(b);
};

const mergeStudentRecordPair = (
  term1: AnalyticsStudentRecord | undefined,
  term2: AnalyticsStudentRecord | undefined,
): AnalyticsStudentRecord | undefined => {
  if (!term1 && !term2) return undefined;
  if (!term1) return term2;
  if (!term2) return term1;

  const merged: AnalyticsStudentRecord = {
    ...term1,
    ...term2,
    term1_competency: term1.term1_competency,
    term2_competency: term2.term2_competency,
    academic_year: term2.academic_year || term1.academic_year,
    term: 0,
  };

  const scorePatch = SCORE_FIELDS.reduce<Record<ScoreField, number>>((acc, field) => {
    acc[field] = mergeNumeric(term1[field], term2[field]);
    return acc;
  }, {} as Record<ScoreField, number>);

  return {
    ...merged,
    ...scorePatch,
  };
};

export const mergeAnalyticsRecordSets = (
  term1Records: AnalyticsStudentRecord[] = [],
  term2Records: AnalyticsStudentRecord[] = [],
): AnalyticsStudentRecord[] => {
  const term1Map = new Map<string, AnalyticsStudentRecord>();
  term1Records.forEach((record) => {
    term1Map.set(String(record.student_id), record);
  });

  const result = new Map<string, AnalyticsStudentRecord>();
  term2Records.forEach((record) => {
    const key = String(record.student_id);
    const merged = mergeStudentRecordPair(term1Map.get(key), record);
    if (merged) result.set(key, merged);
    term1Map.delete(key);
  });

  term1Map.forEach((record, key) => {
    result.set(key, record);
  });

  return Array.from(result.values());
};

export const mergeNationalAnalyticsResponses = (
  term1: NationalAnalyticsResponse | undefined,
  term2: NationalAnalyticsResponse | undefined,
): NationalAnalyticsResponse | undefined => {
  if (!term1 && !term2) return undefined;
  if (!term1) return term2;
  if (!term2) return term1;

  const byRegion = new Map<number, AnalyticsRegionPayload>();

  term1.data.forEach((region) => {
    byRegion.set(region.region_id, {
      region_id: region.region_id,
      roName: region.roName,
      records: region.records || [],
    });
  });

  term2.data.forEach((region) => {
    const existing = byRegion.get(region.region_id);
    if (!existing) {
      byRegion.set(region.region_id, {
        region_id: region.region_id,
        roName: region.roName,
        records: region.records || [],
      });
      return;
    }

    byRegion.set(region.region_id, {
      region_id: region.region_id,
      roName: region.roName || existing.roName,
      records: mergeAnalyticsRecordSets(existing.records || [], region.records || []),
    });
  });

  const data = Array.from(byRegion.values()).sort((a, b) => a.region_id - b.region_id);
  const total = data.reduce((sum, region) => sum + (region.records?.length || 0), 0);

  return {
    success: term1.success || term2.success,
    academic_year: term2.academic_year || term1.academic_year,
    term: 0,
    total,
    data,
    message: term2.message || term1.message,
  };
};

export const mergeRegionalAnalyticsResponses = (
  term1: RegionalAnalyticsResponse | undefined,
  term2: RegionalAnalyticsResponse | undefined,
): RegionalAnalyticsResponse | undefined => {
  if (!term1 && !term2) return undefined;
  if (!term1) return term2;
  if (!term2) return term1;

  const data = mergeAnalyticsRecordSets(term1.data || [], term2.data || []);
  return {
    success: term1.success || term2.success,
    academic_year: term2.academic_year || term1.academic_year,
    term: 0,
    total: data.length,
    data,
    message: term2.message || term1.message,
  };
};

export const mergeAvgStudentAnalyticsResponses = (
  term1: AvgStudentAnalyticsResponse | undefined,
  term2: AvgStudentAnalyticsResponse | undefined,
): AvgStudentAnalyticsResponse | undefined => {
  if (!term1 && !term2) return undefined;
  if (!term1) return term2;
  if (!term2) return term1;

  const merged: AvgStudentAnalyticsResponse = {
    ...term1,
    ...term2,
    success: term1.success || term2.success,
    term: 0,
    hpcData: {
      ...(term1.hpcData || {}),
      ...(term2.hpcData || {}),
    },
  };

  merged.avg_physical_dev_score = mergeNumeric(term1.avg_physical_dev_score, term2.avg_physical_dev_score);
  merged.avg_socio_emotional_score = mergeNumeric(term1.avg_socio_emotional_score, term2.avg_socio_emotional_score);
  merged.avg_cognitive_score = mergeNumeric(term1.avg_cognitive_score, term2.avg_cognitive_score);
  merged.avg_language_score = mergeNumeric(term1.avg_language_score, term2.avg_language_score);
  merged.avg_aesthetic_score = mergeNumeric(term1.avg_aesthetic_score, term2.avg_aesthetic_score);
  merged.avg_learning_habits_score = mergeNumeric(term1.avg_learning_habits_score, term2.avg_learning_habits_score);
  merged.avg_confidence_score = mergeNumeric(term1.avg_confidence_score, term2.avg_confidence_score);
  merged.avg_focus_score = mergeNumeric(term1.avg_focus_score, term2.avg_focus_score);
  merged.avg_peer_interaction_score = mergeNumeric(term1.avg_peer_interaction_score, term2.avg_peer_interaction_score);
  merged.avg_motivation_score = mergeNumeric(term1.avg_motivation_score, term2.avg_motivation_score);
  merged.avg_leadership_score = mergeNumeric(term1.avg_leadership_score, term2.avg_leadership_score);
  merged.avg_participation_score = mergeNumeric(term1.avg_participation_score, term2.avg_participation_score);

  return merged;
};

export const mapAvgStudentAnalyticsToHolisticStudent = (
  response: AvgStudentAnalyticsResponse | undefined,
): HolisticStudent | null => {
  if (!response?.success || !response.student_id) return null;

  const hpc = response.hpcData;

  const physicalDev = scaleScore(response.avg_physical_dev_score);
  const socio = scaleScore(response.avg_socio_emotional_score);
  const cognitive = scaleScore(response.avg_cognitive_score);
  const language = scaleScore(response.avg_language_score);
  const aesthetic = scaleScore(response.avg_aesthetic_score);
  const learning = scaleScore(response.avg_learning_habits_score);

  const competency = avgNonZero([physicalDev, socio, cognitive, language, aesthetic, learning]);
  const behaviorScore = avgNonZero([
    scaleScore(response.avg_confidence_score),
    scaleScore(response.avg_focus_score),
    scaleScore(response.avg_motivation_score),
    scaleScore(response.avg_leadership_score),
  ]);
  const peerScore = scaleScore(response.avg_peer_interaction_score);

  const first = (response.firstName || '').trim();
  const middle = (response.middleName || '').trim();
  const last = (response.lastName || '').trim();
  const fullName = [first, middle, last].filter(Boolean).join(' ') || `Student ${response.student_id}`;

  const className = response.className || getClassDisplayName(Number(response.class_id ?? -1));

  const selectedHandSpanCount = toNumber(response.term === 2 ? hpc?.t2_height_handspan : hpc?.t1_height_handspan)
    || toNumber(hpc?.t2_height_handspan)
    || toNumber(hpc?.t1_height_handspan);
  const avgHandSpanCm = 12.75;
  const estimatedHeightCm = +(selectedHandSpanCount * avgHandSpanCm).toFixed(1);
  const selectedWeight = toNumber(response.term === 2 ? hpc?.t2_weight_kg : hpc?.t1_weight_kg)
    || toNumber(hpc?.t2_weight_kg)
    || toNumber(hpc?.t1_weight_kg);
  const bmi = estimatedHeightCm > 0
    ? +(selectedWeight / ((estimatedHeightCm / 100) * (estimatedHeightCm / 100))).toFixed(1)
    : 0;
  const bmiCategory: 'Underweight' | 'Normal' | 'Overweight' | 'Obese' =
    bmi < 18.5 ? 'Underweight' : bmi < 25 ? 'Normal' : bmi < 30 ? 'Overweight' : 'Obese';

  const attendanceByMonth = parseJsonObject(hpc?.daysAttendedPerMonth);
  let presentDays = 0;
  let activeMonths = 0;
  Object.values(attendanceByMonth).forEach((n) => {
    if (typeof n === 'number') {
      presentDays += n;
      activeMonths += 1;
    }
  });
  const attendancePercentage = activeMonths ? +((presentDays / (activeMonths * 22)) * 100).toFixed(1) : 0;

  const term1CompetencyFromHpc = parseCompetencyValue(hpc?.term1_competency);
  const term2CompetencyFromHpc = parseCompetencyValue(hpc?.term2_competency);
  const term1Competency = term1CompetencyFromHpc || +competency.toFixed(1);
  const term2Competency = term2CompetencyFromHpc || +competency.toFixed(1);
  const effectiveCompetency = +avg([term1Competency, term2Competency]).toFixed(1);

  return {
    id: String(response.student_id),
    name: fullName,
    className,
    section: toText(response.section, '-'),
    rollNo: Number(response.rollNo || 0),
    gender: toGender(response.gender),
    academicYear: String(response.academic_year),
    physical: {
      handSpanCount: selectedHandSpanCount,
      height: estimatedHeightCm,
      weight: selectedWeight,
      bmi,
      bmiCategory,
    },
    behavior: {
      self_with_friends_t1: toText(hpc?.self_with_friends_t1, '-'),
      self_with_friends_t2: toText(hpc?.self_with_friends_t2, '-'),
      self_find_difficult_t1: toText(hpc?.self_find_difficult_t1, '-'),
      self_find_difficult_t2: toText(hpc?.self_find_difficult_t2, '-'),
      self_enjoy_most_t1: toText(hpc?.self_enjoy_most_t1, '-'),
      self_enjoy_most_t2: toText(hpc?.self_enjoy_most_t2, '-'),
    },
    peer: {
      peer_shares_stn_t1: toText(hpc?.peer_shares_stn_t1, '-'),
      peer_shares_stn_t2: toText(hpc?.peer_shares_stn_t2, '-'),
      peer_likes_play_t1: toText(hpc?.peer_likes_play_t1, '-'),
      peer_likes_play_t2: toText(hpc?.peer_likes_play_t2, '-'),
      peer_helps_task_t1: toText(hpc?.peer_helps_task_t1 ?? hpc?.peer_helps_task_t2, '-'),
    },
    parentFeedback: {
      parent_support_t1: toText(hpc?.parent_support_t1, '-'),
      parent_support_t2: toText(hpc?.parent_support_t2, '-'),
      parent_share_t1: toText(hpc?.parent_share_t1, '-'),
      parent_share_t2: toText(hpc?.parent_share_t2, '-'),
      parent_enjoys_t1: toText(hpc?.parent_enjoys_t1, '-'),
      parent_enjoys_t2: toText(hpc?.parent_enjoys_t2, '-'),
      parent_vaccine_t1: toVaccineLabel(hpc?.parent_vaccine_t1),
      parent_vaccine_t2: toVaccineLabel(hpc?.parent_vaccine_t2),
    },
    teacherObservation: {
      lp_strengths_t1: toText(hpc?.lp_strengths_t1, '-'),
      lp_strengths_t2: toText(hpc?.lp_strengths_t2, '-'),
      lp_challenges_t1: toText(hpc?.lp_challenges_t1, '-'),
      lp_challenges_t2: toText(hpc?.lp_challenges_t2, '-'),
      lp_observations_t1: toText(hpc?.lp_observations_t1, '-'),
      lp_observations_t2: toText(hpc?.lp_observations_t2, '-'),
      lp_suggestions_t1: toText(hpc?.lp_suggestions_t1, '-'),
      lp_suggestions_t2: toText(hpc?.lp_suggestions_t2, '-'),
    },
    interests: {
      things_i_like: toText(hpc?.things_i_like, '-'),
      favourite_games: toText(hpc?.favourite_games, '-'),
      favourite_food: toText(hpc?.favourite_food, '-'),
      favourite_colour: toText(hpc?.favourite_colour, '-'),
      favourite_animals: toText(hpc?.favourite_animals, '-'),
      friends_are: toText(hpc?.friends_are, '-'),
      i_live_in: toText(hpc?.i_live_in, '-'),
    },
    academic: {
      term1_competency: term1Competency,
      term2_competency: term2Competency,
      academicYear: String(response.academic_year),
      classID: String(response.class_id ?? ''),
      // Pass raw JSON strings so CompetencyTab can parse individual indicator scores
      term1_competency_raw: typeof hpc?.term1_competency === 'string' ? hpc.term1_competency : undefined,
      term2_competency_raw: typeof hpc?.term2_competency === 'string' ? hpc.term2_competency : undefined,
    },
    attendance: {
      daysAttendedPerMonth: Object.entries(attendanceByMonth).reduce<Record<string, number>>((acc, [month, days]) => {
        acc[month] = typeof days === 'number' ? days : 0;
        return acc;
      }, {}),
      totalSchoolDaysPerMonth: Object.entries(parseJsonObject(hpc?.totalSchoolDaysPerMonth)).reduce<Record<string, number>>((acc, [month, days]) => {
        acc[month] = typeof days === 'number' ? days : 0;
        return acc;
      }, {}),
    },
    photos: {
      photo_myself: hpc?.photo_myself || undefined,
      photo_family: hpc?.photo_family || undefined,
      photo_portfolio: hpc?.photo_portfolio || undefined,
    },
    behaviorScore: +behaviorScore.toFixed(1),
    peerScore: +peerScore.toFixed(1),
    attendancePercentage,
    averageCompetency: effectiveCompetency,
    competencyLevel: toCompetencyLevel(effectiveCompetency),
    needsSupport: effectiveCompetency < 55,
    schoolName: toText(response.schoolName),
    aiScores: {
      confidence: scaleScore(response.avg_confidence_score),
      focus: scaleScore(response.avg_focus_score),
      motivation: scaleScore(response.avg_motivation_score),
      leadership: scaleScore(response.avg_leadership_score),
      peer_interaction: scaleScore(response.avg_peer_interaction_score),
      participation: scaleScore(response.avg_participation_score),
    },
  };
};

export interface FlnTrackingRecord {
  student_id: string;
  first_name?: string;
  middle_name?: string;
  last_name?: string;
  academic_year: number;
  class_id: number;
  school_id: number;
  region_id: number;
  state_id: number;
  
  // Term Columns (for 2025)
  term1_lit1?: string;
  term2_lit1?: string;
  term1_lit2?: string;
  term2_lit2?: string;
  term1_num?: string;
  term2_num?: string;
  overall_lit1?: string;
  overall_lit2?: string;
  overall_num?: string;
  
  // Cycle Columns (for 2026+)
  cycle1_lit1?: string;
  cycle2_lit1?: string;
  cycle3_lit1?: string;
  cycle4_lit1?: string;
  cycle5_lit1?: string;
  cycle6_lit1?: string;
  cycle1_lit2?: string;
  cycle2_lit2?: string;
  cycle3_lit2?: string;
  cycle4_lit2?: string;
  cycle5_lit2?: string;
  cycle6_lit2?: string;
  cycle1_num?: string;
  cycle2_num?: string;
  cycle3_num?: string;
  cycle4_num?: string;
  cycle5_num?: string;
  cycle6_num?: string;
}

export interface FlnClassTrackingResponse {
  success: boolean;
  academic_year: number;
  class_id: number;
  school_id: number;
  data: FlnTrackingRecord[];
  message?: string;
}

export interface FlnStudentTrackingResponse {
  success: boolean;
  academic_year: number;
  student_id: string;
  data: FlnTrackingRecord;
  message?: string;
}

export interface NationalStudentsHpcNationalResponse {
  success: boolean;
  academic_year: number;
  totalStudents: number;
  totalBalvatikaStudents: number;
  totalClass3to5Students: number;
  totalClass6to11Students: number;
  tableBreakdown: {
    [key: string]: {
      total: number;
    };
  };
  message?: string;
}
// ── Foundation per-competency analytics (hpc_* rubric aggregation) ─────────────
/** [entryB, entryP, entryPr, exitB, exitP, exitPr] */
export type CompetencyCountsTuple = [number, number, number, number, number, number];

export interface ClassCompetencyAnalytics {
  classId: number;
  total: number;
  competencies: Record<string, CompetencyCountsTuple>;
}

export interface FoundationCompetencyAnalyticsPayload {
  year: number;
  academicYear: string;
  scope: { roid?: number; kvid?: number };
  classes: ClassCompetencyAnalytics[];
}

export interface FoundationCompetencyAnalyticsResponse {
  success: boolean;
  data: FoundationCompetencyAnalyticsPayload;
  message?: string;
}

/**
 * Foundational Stage national dashboard, pre-aggregated by the server
 * (GET /sis-model/analytics-national-summary). The same numbers
 * mapNationalAnalyticsToFoundationOverview + the dashboard's region trend used to
 * compute from every student record, in ~25 KB instead of two ~125 MB payloads.
 */
export interface RegionCompetencyTrendPoint {
  regionId: number;
  /** Typed like AnalyticsRegionPayload.roName, which the chart was fed before. */
  regionName: string;
  avgCompetency: number;
  topSchoolName: string;
  topSchoolScore: number;
  worstSchoolName: string;
  worstSchoolScore: number;
}

export interface NationalSummaryResponse {
  success: boolean;
  academic_year: number;
  /** 1, 2, or 0 for Overall. */
  term: 0 | 1 | 2;
  totalStudents: number;
  availableAcademicYears: number[];
  overview: NationalOverview;
  regionCompetencyTrend: RegionCompetencyTrendPoint[];
  message?: string;
}

export const holisticApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getNationalHolisticSummary: build.query<NationalSummaryResponse, { academicYear: number; term: TermFilter }>({
      query: ({ academicYear, term }) =>
        `/sis-model/analytics-national-summary?academic_year=${academicYear}&term=${termFilterToQueryValue(term)}`,
      providesTags: ['Student'],
      keepUnusedDataFor: 300,
    }),
    getNationalHolisticAnalytics: build.query<NationalAnalyticsResponse, NationalAnalyticsQueryArgs>({
      query: ({ academicYear, term }) =>
        `/sis-model/analytics-national?academic_year=${academicYear}&term=${term}`,
      providesTags: ['Student'],
      keepUnusedDataFor: 300,
    }),
    getNationalStudentsHpcNational: build.query<NationalStudentsHpcNationalResponse, { academicYear: number }>({
      query: ({ academicYear }) =>
        `/sis-model/totalstudents-hpc-national?academic_year=${academicYear}`,
      providesTags: ['Student'],
      keepUnusedDataFor: 300,
    }),
    getRegionalHolisticAnalytics: build.query<RegionalAnalyticsResponse, RegionalAnalyticsQueryArgs>({
      query: ({ regionId, academicYear, term }) =>
        `/sis-model/analytics-region/${regionId}?academic_year=${academicYear}&term=${term}`,
      providesTags: ['Student'],
      keepUnusedDataFor: 300,
    }),
    getClassHolisticAnalytics: build.query<RegionalAnalyticsResponse, ClassAnalyticsQueryArgs>({
      query: ({ classId, academicYear, term, schoolId }) =>
        `/sis-model/analytics-class/${classId}?academic_year=${academicYear}&term=${term}${schoolId ? `&schoolid=${schoolId}` : ''}`,
      providesTags: ['Student'],
      keepUnusedDataFor: 300,
    }),
    getAvgAnalyticsStudent: build.query<AvgStudentAnalyticsResponse, StudentAnalyticsQueryArgs>({
      query: ({ studentId, academicYear, term }) =>
        `/sis-model/avg-analytics-student/${studentId}?academic_year=${academicYear}&term=${term}`,
      providesTags: ['Student'],
      keepUnusedDataFor: 300,
    }),
    getHpcYears: build.query<{ success: boolean; data: number[]; message?: string }, void>({
      query: () => '/sis-model/hpc-years',
      providesTags: ['Student'],
      keepUnusedDataFor: 300,
    }),
    getFoundationCompetencyAnalytics: build.query<
      FoundationCompetencyAnalyticsResponse,
      { year: number; roid?: number; kvid?: number }
    >({
      query: ({ year, roid, kvid }) => {
        const params = new URLSearchParams({ year: String(year) });
        if (roid !== undefined) params.set('roid', String(roid));
        if (kvid !== undefined) params.set('kvid', String(kvid));
        return `/kvsresult/foundation-competency-analytics?${params.toString()}`;
      },
      providesTags: ['Student'],
      keepUnusedDataFor: 600,
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetNationalHolisticSummaryQuery,
  useGetNationalHolisticAnalyticsQuery,
  useGetNationalStudentsHpcNationalQuery,
  useGetRegionalHolisticAnalyticsQuery,
  useGetClassHolisticAnalyticsQuery,
  useGetAvgAnalyticsStudentQuery,

  useGetHpcYearsQuery,
  useGetFoundationCompetencyAnalyticsQuery,
} = holisticApi;


